import React, { memo, useMemo } from 'react';
import { useStyles } from 'uinix-ui';

import { SharedFooterProps } from '../../types/SharedFooterProps';
import { SharedHeaderProps } from '../../types/SharedHeaderProps';
import { StatusType } from '../../types/StatusType';
import { Footer } from '../Footer/Footer';
import { Header } from '../Header/Header';
import { Layout } from '../Layout/Layout';
import { Text } from '../Text/Text';

export interface Props {
  /** Card border color */
  borderColor?: string;
  /** Card contents */
  children: React.ReactNode;
  /** Supported semantic DOM element */
  as?: 'details';
  /** Banner text */
  banner?: {
    /** A11y-friendly label */
    label: string;
    /** Banner status display color */
    status: StatusType;
    /** Banner display text */
    text: string;
  };
  /** Disabled state */
  disabled?: boolean;
  /** DOM props when used in conjunction as `props.as` */
  domProps?: Partial<{
    'aria-selected': boolean;
    open: boolean;
  }>;
  /** Keyboard handler for interactive cards */
  onKeyDown?: (e: React.KeyboardEvent) => void;
  /** Tab index for keyboard navigation */
  tabIndex?: number;
  /** Flex */
  flex?: string;
  /** Optional card footer */
  footer?: SharedFooterProps;
  /** Optional card header */
  header?: SharedHeaderProps;
  /** UI mode */
  mode?: 'bordered' | 'embedded' | 'raised' | 'featured';
  /** Optional accesibility name to define the current element */
  name?: string;
  /** Override border radius (e.g. to keep rounded corners with smaller size) */
  borderRadius?: 'm' | 'ml' | 'l' | 'pill';
  /** Configures card size (e.g. padding) */
  size?: 's' | 'm' | 'l';
  /** Optional status indicator */
  status?: StatusType;
  /** CSS width value */
  width?: number | string;
  /** Optional accesibility role to define the current element */
  role?: string;
  /** Click handler */
  onClick?: (event?: MouseEvent) => void;
}

export const Card = memo(
  ({
    as,
    banner,
    borderColor,
    children,
    disabled,
    domProps = {},
    flex,
    onKeyDown,
    footer,
    header,
    mode = 'raised',
    name,
    onClick,
    borderRadius: borderRadiusProp = undefined,
    size = 'm',
    status,
    tabIndex,
    width,
    role = 'region',
  }: Props) => {
    const styles = useStyles();

    const isEmbedded = mode === 'embedded';
    const isFeatured = mode === 'featured' && !isEmbedded;
    const hasBorder = (mode === 'bordered' || isFeatured) && !isEmbedded;

    const px = useMemo(() => {
      if (isEmbedded) return 0;

      switch (size) {
        case 's':
          return 3;
        case 'm':
          return 6;
        case 'l':
          return 6;
        default:
          return 3;
      }
    }, [size, isEmbedded]);

    const py = useMemo(() => {
      if (isEmbedded) return 0;

      switch (size) {
        case 's':
          return 2;
        case 'm':
          return 6;
        case 'l':
          return 6;
        default:
          return 2;
      }
    }, [size, isEmbedded]);

    const getBoxShadow = () => {
      if (isEmbedded) return 'none';
      if (isFeatured) return 'subtle';
      if (mode === 'raised') return 'raised';
      return 'none'; // for bordered mode
    };

    const borderRadius = borderRadiusProp ?? (size === 's' ? 'm' : size);
    const cornerRadius = borderRadiusProp ?? size;
    const componentStyles = {
      base: {
        background: 'background',
        border: hasBorder ? 'border' : undefined,
        borderRadius,
        borderWidth: isFeatured ? 'm' : 's',
        borderTopLeftRadius: banner ? 0 : cornerRadius,
        borderTopRightRadius: banner ? 0 : cornerRadius,
        borderColor,
        boxShadow: getBoxShadow(),
        minWidth: width,
        overflowY: banner ? 'visible' : 'auto',
        position: 'relative',
        width,
        '&:hover': {
          boxShadow: Boolean(onClick) ? 'raised' : undefined,
        },
      },
      banner: {
        borderTopLeftRadius: cornerRadius,
        borderTopRightRadius: cornerRadius,
        height: 'card.banner.height',
        left: '-1px',
        paddingLeft: 2,
        paddingRight: 2,
        paddingTop: 0.5,
        position: 'absolute',
        right: '-1px',
        textAlign: 'center',
        top: 0,
        transform: 'translateY(-100%)',
      },
    };

    return (
      <Layout
        {...domProps}
        aria-label={name}
        onKeyDown={onKeyDown}
        tabIndex={tabIndex}
        as={as}
        role={role}
        direction="column"
        disabled={disabled}
        flex={flex}
        styles={[componentStyles.base, styles.colors.status]}
        styleProps={{ status }}
        onClick={disabled ? undefined : onClick}
      >
        {banner && (
          <Text
            aria-label={banner.label}
            color="inverse.text.primary"
            styles={[componentStyles.banner, styles.colors.status]}
            styleProps={{ status: banner.status, variant: 'primary' }}
            variant="tiny-bold"
          >
            {banner.text}
          </Text>
        )}
        {header && <Header {...header} />}
        {children && (
          <Layout direction="column" flex="auto" px={px} py={py} w="100%">
            {children}
          </Layout>
        )}
        {footer && <Footer {...footer} />}
      </Layout>
    );
  },
);
